<template>
  <div class="panel panel-default">

    <!-- Correct answer banner (practice / random only - speed auto-advances) -->
    <transition name="fade">
      <div v-if="answered && !speedMode" class="feedback feedback-correct text-center">
        <span class="feedback-text">✓ Correct! Well done!</span>
        <button class="next-btn" @click="nextQuestion">Next Question →</button>
      </div>
    </transition>

    <!-- Wrong answer flash -->
    <transition name="fade">
      <div v-if="wrongFlash" class="feedback feedback-wrong text-center">
        <span class="feedback-text">✗ Wrong! Try again.</span>
      </div>
    </transition>

    <div class="panel-heading">
      <h3 class="panel-title text-center question-text">{{ question }}</h3>
    </div>

    <div class="panel-body answer-grid">
      <button
        v-for="(btn, index) in btnData"
        :key="index"
        class="ans-btn"
        :class="{
          'ans-btn--correct': answered && btn.correct,
          'ans-btn--wrong':   wrongFlash && btn === lastWrong,
        }"
        :disabled="answered && !speedMode"
        @click="onAnswer(btn)"
      >
        {{ btn.answer }}
      </button>
    </div>

  </div>
</template>

<script>
function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function shuffle(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export default {
  props: {
    // 1-12 = specific table; 0 = random table each question
    table: {
      type: Number,
      default: 2,
    },
    speedMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      answered:   false,
      wrongFlash: false,
      lastWrong:  null,
      question:   "",
      btnData:    [],
      _flashTimer: null,
    };
  },

  methods: {
    generateQuestion() {
      this.answered   = false;
      this.wrongFlash = false;
      this.lastWrong  = null;
      clearTimeout(this._flashTimer);

      const tbl        = this.table === 0 ? randInt(1, 12) : this.table;
      const multiplier = randInt(1, 12);
      const correct    = tbl * multiplier;

      this.question = `What is ${tbl} × ${multiplier}?`;

      // Build 3 distinct wrong answers close to the correct one
      const wrongs = new Set();
      while (wrongs.size < 3) {
        const off   = randInt(1, 5) * (Math.random() > 0.5 ? 1 : -1) * tbl;
        const wrong = correct + off;
        if (wrong > 0 && wrong !== correct) wrongs.add(wrong);
      }

      this.btnData = shuffle([
        { correct: true,  answer: correct },
        ...[...wrongs].map(w => ({ correct: false, answer: w })),
      ]);
    },

    onAnswer(btn) {
      if (btn.correct) {
        this.answered = true;
        this.$emit("correct");

        // In speed mode the parent remounts us immediately, nothing else to do
      } else {
        // Wrong - flash, reset, keep playing
        this.lastWrong  = btn;
        this.wrongFlash = true;
        this.$emit("wrong");

        this._flashTimer = setTimeout(() => {
          this.wrongFlash = false;
          this.lastWrong  = null;
          // Regenerate in speed mode so a wrong answer can't be gamed by exclusion
          if (this.speedMode) this.generateQuestion();
        }, 600);
      }
    },

    nextQuestion() {
      this.generateQuestion();
    },
  },

  created() {
    this.generateQuestion();
  },

  beforeDestroy() {
    clearTimeout(this._flashTimer);
  },
};
</script>

<style scoped>
/* ── Feedback banners ─────────────────────────────────────────────── */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }

.feedback {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 14px;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 17px;
  font-weight: bold;
}
.feedback-correct {
  background: rgba(3, 120, 60, 0.12);
  color: rgb(3, 100, 50);
  border: 2px solid rgba(3, 120, 60, 0.3);
}
.feedback-wrong {
  background: rgba(180, 30, 30, 0.1);
  color: rgb(160, 20, 20);
  border: 2px solid rgba(180, 30, 30, 0.3);
}
.feedback-text { font-size: 1.1rem; }

/* ── Question text ────────────────────────────────────────────────── */
.question-text {
  font-size: 26px;
  font-weight: 700;
  color: rgb(3, 53, 48);
  padding: 18px 12px 10px;
  letter-spacing: 1px;
}

/* ── Answer grid ──────────────────────────────────────────────────── */
.answer-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 12px 16px 16px;
}
.ans-btn {
  min-height: 60px;
  font-size: 20px;
  font-weight: 700;
  border-radius: 8px;
  border: 2px solid rgb(3, 53, 48);
  background: rgb(169, 247, 234);
  color: rgb(3, 53, 48);
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
}
.ans-btn:hover:not(:disabled) {
  background: rgb(100, 225, 205);
  transform: scale(1.04);
}
.ans-btn:disabled { opacity: 0.55; cursor: default; }

.ans-btn--correct {
  background: rgb(3, 53, 48) !important;
  color: white !important;
  border-color: rgb(3, 53, 48) !important;
}
.ans-btn--wrong {
  background: rgb(220, 50, 50) !important;
  color: white !important;
  border-color: rgb(180, 20, 20) !important;
  animation: shake 0.3s ease;
}

/* ── Next button ─────────────────────────────────────────────────── */
.next-btn {
  background-color: rgb(3, 53, 48);
  color: white;
  padding: 10px 28px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  white-space: nowrap;
}
.next-btn:hover { background-color: rgb(5, 80, 70); }

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25%       { transform: translateX(-5px); }
  75%       { transform: translateX(5px); }
}
</style>
